import parse, { DOMNode, domToReact, Element } from 'html-react-parser';
import DOMPurify from 'isomorphic-dompurify';
import { c } from '../../../utils/tailwind-merge';
import type { RichTextProps } from './RichText.types';

export type {
  RichTextLinkComponentProps,
  RichTextProps,
} from './RichText.types';

/**
 * Renders rich text content as HTML.
 */
export const RichText = ({
  html,
  className = '',
  type = 'div',
  linkComponent,
  ...props
}: RichTextProps) => {
  const Component = type;
  const sanitizedHtml = DOMPurify.sanitize(html);

  const parseOptions = linkComponent
    ? {
        replace: (domNode: DOMNode) => {
          if (
            domNode instanceof Element &&
            domNode.name === 'a' &&
            domNode.attribs?.href
          ) {
            const { href, target, rel } = domNode.attribs;
            const children = domNode.children
              ? domToReact(domNode.children as DOMNode[], parseOptions)
              : null;

            return linkComponent({
              href,
              target,
              rel,
              children: children || '',
            });
          }
        },
      }
    : undefined;

  const content = linkComponent
    ? parse(sanitizedHtml, parseOptions)
    : undefined;

  return (
    <Component
      data-test="rich-text"
      className={c(
        'body-small [&_h2]:header-mid [&_h3]:header-small [&_h4]:header-xsmall text-grey-900 [&_h2]:mb-5 [&_h3]:mb-5 lg:[&_h3]:mb-2 [&_li>p]:mb-1 [&_li]:ml-8 [&_ol]:mb-5 [&_ol]:list-decimal [&_p]:mb-4 [&_ul]:mb-5 [&_ul]:list-disc',
        !linkComponent && '[&_a]:body-small-link [&_a]:text-blue-hover ',
        className
      )}
      {...(content
        ? { children: content }
        : { dangerouslySetInnerHTML: { __html: sanitizedHtml } })}
      {...props}
    />
  );
};
